/**
 * Cobros de Matrícula — pantalla unificada de cobro, consulta y anulación.
 *
 * La vista principal es el listado de cobros vigentes embebido
 * (cancelarcobromatricula, ares6040: búsqueda por chasis/nombre o fechas,
 * reimpresión y anulación con razón). Tarjetas de acción:
 *   - "Cobrar matrícula" (ares6010): el formulario de cobro en diálogo a
 *     pantalla completa; al guardar, el listado se recarga.
 *   - "Informe de cobros" (ares6020): el listado de matrículas cobradas con
 *     export PDF, en diálogo con barra Volver.
 *
 * Sustituye las entradas de menú de cobromatricula.js,
 * cancelarcobromatricula.js e informematriculascobradas.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import CobroMatricula from "modules/matriculas/cobromatricula";
import CancelarCobroMatricula from "modules/matriculas/cancelarcobromatricula";
import InformeMatriculasCobradas from "modules/motores/informematriculascobradas";
import useAccionInicial from "lib/useAccionInicial";

export default function MatriculasCobros() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeCobrar = tienePermisosModulo(roleDetalles, "ares6010");
  const puedeAnular = tienePermisosModulo(roleDetalles, "ares6040");
  const puedeInforme = tienePermisosModulo(roleDetalles, "ares6020");

  const [cobroOpen, setCobroOpen] = useState(false);
  const [informeOpen, setInformeOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    cobrar: { permitido: puedeCobrar, abrir: () => setCobroOpen(true) },
    informe: { permitido: puedeInforme, abrir: () => setInformeOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras cobrar una matrícula).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cobros de Matrícula"
        subtitle="Cobra, consulta y anula cobros de matrícula en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeCobrar || puedeInforme) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeCobrar && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={BadgeOutlinedIcon}
                  titulo="Cobrar matrícula"
                  descripcion="Registra el cobro de la matrícula de un vehículo facturado"
                  color={FP.color.success}
                  onClick={() => setCobroOpen(true)}
                />
              </Grid>
            )}
            {puedeInforme && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={AssessmentOutlinedIcon}
                  titulo="Informe de cobros"
                  descripcion="Listado de matrículas cobradas con export a PDF"
                  color={FP.color.accentBlue}
                  onClick={() => setInformeOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: cobros vigentes con anular/reimprimir por fila */}
        {puedeAnular && <CancelarCobroMatricula embedded refreshToken={refreshKey} />}
      </Box>

      {/* Cobrar matrícula (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={cobroOpen} onClose={() => setCobroOpen(false)}>
        <CobroMatricula embedded onExit={() => { setCobroOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Informe de matrículas cobradas (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={informeOpen} onClose={() => setInformeOpen(false)}>
        <BarraVolver onExit={() => setInformeOpen(false)} />
        <InformeMatriculasCobradas />
      </Dialog>
    </Box>
  );
}
